import { useState, useEffect } from 'react'
import { Bookmark, BookmarkCheck } from 'lucide-react'
import { useAppStore } from '@cya-routes/shared'
import { toggleBookmark, checkBookmarkExists, BookmarkInput } from '../../services/firebase/bookmarks'

interface BookmarkButtonProps {
  routeId: string
  routeData: BookmarkInput
  size?: 'sm' | 'md' | 'lg'
  className?: string
  showLabel?: boolean
  variant?: 'default' | 'overlay' | 'icon'
  onBookmarkChange?: (isBookmarked: boolean) => void
}

export function BookmarkButton({
  routeId,
  routeData,
  size = 'md',
  className = '',
  showLabel = false,
  variant = 'default',
  onBookmarkChange
}: BookmarkButtonProps) {
  const { auth } = useAppStore()
  const [isBookmarked, setIsBookmarked] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)

  // Check if route is bookmarked on mount and when auth changes
  useEffect(() => {
    const checkBookmark = async () => {
      if (auth.currentUser?.id && routeId) {
        try {
          const bookmarked = await checkBookmarkExists(auth.currentUser.id, routeId)
          setIsBookmarked(bookmarked)
        } catch (error) {
          console.error('Error checking bookmark status:', error)
        }
      } else {
        setIsBookmarked(false)
      }
    }

    checkBookmark()
  }, [auth.currentUser?.id, routeId])

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent triggering parent click handlers
    
    // Check if user is authenticated
    if (!auth.currentUser) {
      setShowLoginPrompt(true)
      setTimeout(() => setShowLoginPrompt(false), 3000)
      return
    }

    setIsLoading(true)
    try {
      const newBookmarkState = await toggleBookmark(auth.currentUser.id, routeData)
      setIsBookmarked(newBookmarkState)
      onBookmarkChange?.(newBookmarkState)
    } catch (error) {
      console.error('Error toggling bookmark:', error)
    } finally {
      setIsLoading(false)
    }
  }

  // Size configurations
  const sizeConfig = {
    sm: {
      icon: 'w-3.5 h-3.5',
      button: 'p-1',
      text: 'text-xs'
    },
    md: {
      icon: 'w-5 h-5',
      button: 'p-2',
      text: 'text-sm'
    },
    lg: {
      icon: 'w-6 h-6',
      button: 'p-3',
      text: 'text-base'
    }
  }

  const config = sizeConfig[size]

  // Variant styles
  const variantStyles = {
    default: `
      ${isBookmarked 
        ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
        : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
      }
      rounded-lg transition-all duration-200 relative
      ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
    `,
    overlay: `
      ${isBookmarked 
        ? 'bg-emerald-600/90 text-white' 
        : 'bg-black/50 text-white hover:bg-black/70'
      }
      backdrop-blur-sm rounded-full transition-all duration-200 relative
      ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
    `,
    icon: `
      ${isBookmarked 
        ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30 hover:bg-emerald-600/30' 
        : 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10 hover:text-white'
      }
      rounded-lg transition-all flex items-center justify-center backdrop-blur-sm border
      ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
    `
  }

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`
          ${config.button}
          ${variantStyles[variant]}
          ${className}
          group flex items-center gap-2
        `}
        title={isBookmarked ? 'Remove bookmark' : 'Bookmark this route'}
      >
        {isLoading ? (
          <div className={`${config.icon} animate-spin`}>
            <svg className="w-full h-full" fill="none" viewBox="0 0 24 24">
              <circle 
                className="opacity-25" 
                cx="12" 
                cy="12" 
                r="10" 
                stroke="currentColor" 
                strokeWidth="4"
              />
              <path 
                className="opacity-75" 
                fill="currentColor" 
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        ) : isBookmarked ? (
          <BookmarkCheck className={`${config.icon} ${variant === 'default' ? 'group-hover:scale-110' : ''} transition-transform`} />
        ) : (
          <Bookmark className={`${config.icon} ${variant === 'default' ? 'group-hover:scale-110' : ''} transition-transform`} />
        )}
        
        {showLabel && (
          <span className={config.text}>
            {isBookmarked ? 'Bookmarked' : 'Bookmark'}
          </span>
        )}
      </button>

      {/* Login prompt tooltip */}
      {showLoginPrompt && (
        <div className="absolute z-50 -top-10 left-1/2 transform -translate-x-1/2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg whitespace-nowrap animate-fade-in">
          Sign in to bookmark routes
          <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
        </div>
      )}
    </>
  )
}